import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { BlankSlate, Box, Text } from '@factorialco/deprecated-design-system'
import { F0Card, TwoColumnLayout } from '@factorialco/f0-react'
import { DropdownItem, OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import {
  Add,
  AlertCircleLine,
  ArrowDown,
  ArrowUp,
  Delete,
  Folders,
  Pencil,
} from '@factorialco/f0-react/icons/app'
import {
  FeatureFlagAvailabilitiesDocument,
  FormsElementTypeEnum,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useAiAgentFeature } from 'modules/ai/hooks/useAiAgentFeature'
import { useCocreationAgent } from 'modules/ai_reports/lib/useCocreationAgent'
import useDeleteElement from 'modules/forms/hooks/useDeleteElement'
import {
  QuestionnaireType,
  buildStateElements,
  elementFromData,
  useLoadForm,
  useUpdateForm,
} from 'modules/forms/hooks/useForms'
import useUpdateElement from 'modules/forms/hooks/useUpdateElement'
import { Element } from 'modules/forms/lib/Element'
import { Form } from 'modules/forms/lib/Form'
import { SectionElement } from 'modules/forms/lib/elements/SectionElement'
import { formatDateToString, formatTimeToString } from 'modules/forms/lib/utils/dates'
import { calculateInsertPosition } from 'modules/forms/lib/utils/positionUtils'

import { EditorHeader } from './EditorHeader'
import SidebarForm, { selectedElementData } from './SidebarForm'
import ElementEditor from './editor/ElementEditor'
import DeleteDialog from './modals/DeleteDialog'
import { ElementPreview } from './preview/ElementPreview'

type MoveCommand = 'top' | 'up' | 'down' | 'bottom'

interface EmptyStateText {
  title?: string
  description?: string
  buttonLabel?: string
}

interface CommonFormProps {
  permittedFieldTypes?: FormsElementTypeEnum[]
  isSaving?: boolean
  hasUnsavedChanges?: boolean
  emptyStateText?: EmptyStateText
  showElementName?: boolean
  titleSize?: 'default' | 'small'
  hideSidebar?: boolean
  showAddQuestionModal?: boolean
  onCloseAddQuestionModal?: () => void
  showAllElements?: boolean
  withContentPadding?: boolean
}

// if we want to load and save the form 'externally'
interface ExternalFormProps extends CommonFormProps {
  form: Form | undefined
  setForm: (form: Form) => void
  formId?: never
  resourceType?: never
}

//if you only have the form id, all the work will be done by the editor
interface InternalFormProps extends CommonFormProps {
  form?: never
  setForm?: never
  formId: string
  resourceType: string
}

export type AgentStateType = {
  questionnaire: QuestionnaireType
  updateForm: boolean
}

type FormEditorProps = ExternalFormProps | InternalFormProps

/**
 * Converts a Form object to the QuestionnaireType format used by the AI agent
 */
const getAiStateFromForm = (formToUpdate: Form | undefined): QuestionnaireType => {
  if (!formToUpdate) {
    return {
      title: '',
      description: '',
      elements: [],
    }
  }
  return {
    title: formToUpdate.title,
    description: formToUpdate.description || '',
    elements: formToUpdate.elements.map((e) => {
      return {
        id: e.id,
        label: e.label,
        description: e.description,
        type: e.getType(),
        position: e.position,
        sectionId: undefined, // TODO
        mandatory: e.mandatory,
        params: e.params,
        options: e.options,
        hasCorrectAnswer: e.hasCorrectAnswer,
      }
    }),
  }
}

/**
 * Internal FormEditorContent component that contains the actual form editor logic
 * This is separated so we can check feature flags first in the wrapper
 * Exported for use by FormEditorWithAgent
 */
export const FormEditorContent: React.FC<
  FormEditorProps & {
    coCreationFeatureAvailable: boolean
    state: AgentStateType | undefined
    setState: ((state: AgentStateType) => void) | undefined
  }
> = ({
  form,
  setForm,
  formId,
  resourceType,
  permittedFieldTypes,
  isSaving = false,
  hasUnsavedChanges = false,
  emptyStateText,
  showElementName = true,
  titleSize = 'default',
  hideSidebar = false,
  showAddQuestionModal = false,
  showAllElements = false,
  withContentPadding = true,
  onCloseAddQuestionModal,
  coCreationFeatureAvailable,
  state,
  setState,
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { form: loadedForm, loadingForm } = useLoadForm({ formId, resourceType })
  form ||= loadedForm
  const internalForm = loadedForm !== undefined

  const [selectedElement, setSelectedElement] = useState<selectedElementData | undefined>(undefined)
  const [pendingSelection, enqueueSelection] = useState<selectedElementData | undefined>(undefined)
  const [openDialog, setOpenDialog] = useState<selectedElementData | undefined>(undefined)
  const [elementToEdit, setElementToEdit] = useState<selectedElementData | undefined>(undefined)
  const [elementToCreate, setElementToCreate] = useState<boolean>(
    searchParams.get('create') === 'true'
  )
  const [addingToSectionIndex, setAddingToSectionIndex] = useState<number | undefined>(undefined)

  const {
    access: { locale },
  } = useCurrentSession()

  useEffect(() => {
    if (showAddQuestionModal) {
      setElementToCreate(true)
    }
  }, [showAddQuestionModal])

  useEffect(() => {
    if (state && form && state.updateForm && coCreationFeatureAvailable) {
      updateFormFromAgent(form)
    }
  }, [state])

  useEffect(() => {
    if (state && form && coCreationFeatureAvailable && setState) {
      setState({ questionnaire: getAiStateFromForm(form), updateForm: false })
    }
  }, [form])

  useEffect(() => {
    //clear selected element if does not exists in form
    if (pendingSelection && form) {
      if (pendingSelection.index !== -1 && pendingSelection.sectionIndex !== -1) {
        // -1 , -1 special value to clear selection
        const section =
          pendingSelection.sectionIndex !== undefined
            ? form.elements[pendingSelection.sectionIndex]
            : undefined
        const elements = section instanceof SectionElement ? section.elements : form.elements
        if (elements[pendingSelection.index]) {
          setSelectedElement({
            index: pendingSelection.index,
            sectionIndex: pendingSelection.sectionIndex,
          })
          enqueueSelection(undefined)
          return
        }
      }
      setSelectedElement(undefined)
      enqueueSelection(undefined)
      return
    }

    // Auto-select first element if no element is selected. The 'All' option has been removed.
    //I don't want to delete the code wich renders all elements because we'll need it for quiz in the LMS content builder.
    //Maybe we can add a prop to the FormEditor component to say if we want to render all elements or one by one.
    if (form && !selectedElement && !pendingSelection && form.elements.length > 0) {
      setSelectedElement({ index: 0, sectionIndex: undefined })
    }
  }, [form, selectedElement, pendingSelection])

  const selectElement = (index: number | undefined, sectionIndex: number | undefined) => {
    if (index === undefined) {
      setSelectedElement(undefined)
    } else if (form) {
      // Check that the element we want to select exists in the form
      // ( The dropdown on the sidebar item calls the onclick handler after we set another selected item )
      // ( is the problem related with the event propagation? )
      const section = sectionIndex !== undefined ? form.elements[sectionIndex] : undefined
      const elements = section instanceof SectionElement ? section.elements : form.elements
      if (elements[index]) {
        setSelectedElement({ index, sectionIndex })
      } else {
        setSelectedElement(undefined)
      }
    }
  }

  const updateFormFromAgent = (currentForm: Form) => {
    if (setForm && state) {
      setForm(
        new Form(
          currentForm.id,
          state.questionnaire.title,
          state.questionnaire.description,
          currentForm.resourceType,
          currentForm.resourceId,
          currentForm.formType,
          currentForm.status,
          currentForm.anonymous,
          buildStateElements(state.questionnaire.elements),
          { ...currentForm.params },
          currentForm.configuration
        )
      )
    }
  }

  const updateExternalForm = (currentForm: Form) => {
    if (setForm) {
      //create new form to force state change
      setForm(
        new Form(
          currentForm.id,
          currentForm.title,
          currentForm.description,
          currentForm.resourceType,
          currentForm.resourceId,
          currentForm.formType,
          currentForm.status,
          currentForm.anonymous,
          [...currentForm.elements],
          { ...currentForm.params },
          currentForm.configuration
        )
      )
      // Mark that there are unsaved changes for external forms
    }
  }

  // called when order changed in sidebar to store new positions
  const { updateForm } = useUpdateForm()
  const onFormUpdated = async () => {
    if (form) {
      if (internalForm) {
        // Internal mode - save to backend immediately
        await updateForm(form)
      } else {
        // External mode - notify parent of changes, no backend saving
        updateExternalForm(form)
      }
    }
  }

  const { update } = useUpdateElement()
  const { deleteElement } = useDeleteElement(form?.id || '')

  if (loadingForm) {
    return <Loading expand />
  }

  if (!form) {
    return (
      <Box height="full" justifyContent="center" alignItems="center">
        <BlankSlate
          illustration="sign-post"
          title={i18n.t('forms.editor.not_found.title')}
          description={i18n.t('forms.editor.not_found.description')}
        />
      </Box>
    )
  }

  /** return the element by index and sectionIndex */
  const getSelectedElement = (index: number, sectionIndex: number | undefined): Element | null => {
    const section = sectionIndex !== undefined ? form.elements[sectionIndex] : undefined
    const element =
      section instanceof SectionElement ? section.elements[index] : form.elements[index]
    if (!element) {
      // Clear selection if element doesn't exist instead of throwing
      setSelectedElement(undefined)
      return null
    }
    return element
  }

  // Save the new element in the form when created from empty
  const handleElementCreate = async (element: Element) => {
    // 'section' will be 'undefined'  if adding to form, otherwise a SectionElement
    const section =
      addingToSectionIndex !== undefined ? form.elements[addingToSectionIndex] : undefined

    // 'elements' is the list of elements we are working on (form.elements or section.elements)
    const elements = section instanceof SectionElement ? section.elements : form.elements

    // Calculate the postion to insert (before any locked element)
    const insertPosition = calculateInsertPosition(elements)

    // Update 'sectionId' (This does not matter because when the form is saved, the elements
    // are sent nested and it does not depend on ‘sectionId’)
    element.sectionId = section instanceof SectionElement ? section.id : undefined

    // Insert the element in the correct position
    elements.splice(insertPosition, 0, element)

    // Ensure positions are valid (This does not matters because when the form is saved
    // the position is updated to the right order)
    elements.forEach((el, i) => {
      el.position = i
    })

    //enqueue change selection after form loaded
    enqueueSelection({ index: insertPosition, sectionIndex: addingToSectionIndex })

    // Close modal and reset section index
    setElementToCreate(false)
    setAddingToSectionIndex(undefined)
    onCloseAddQuestionModal?.()

    // Update form (will save or notify parent based on mode)
    await onFormUpdated()
  }

  /** save the modified element within the modal */
  const elementUpdated = async (element: Element) => {
    if (internalForm) {
      const response = await update({
        ...element.toBackend(),
        id: element.id ?? '',
        resourceId: form.resourceId || undefined,
        resourceType: form.resourceType,
        sectionId: element.sectionId || undefined,
      })

      const result = handleResult(response, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
        })
      })
      if (result.success) {
        //close editor once the element has been saved in backend
        setElementToEdit(undefined)
      }
    } else if (elementToEdit) {
      //if we enter here, the modal to edit element call us, so the 'elementToEdit' is not undefined
      const section =
        elementToEdit.sectionIndex !== undefined
          ? form.elements[elementToEdit.sectionIndex]
          : undefined
      const elements = section instanceof SectionElement ? section.elements : form.elements
      // store the element calling the function (original element cloned inside editor modal)
      // in the same position
      elements[elementToEdit.index] = element
      // notify update
      updateExternalForm(form)
      //close editor once the element has been saved in backend
      setElementToEdit(undefined)
    }
  }

  /** Delete the element  */
  const elementDelete = (index: number, sectionIndex: number | undefined) => {
    if (internalForm) {
      // open the dialog that will remove the element from the backend in 'elementDeleteFromBackend'
      setOpenDialog({ index, sectionIndex })
    } else {
      // external form, remove from memory and update state

      // check if section
      const section = sectionIndex !== undefined ? form.elements[sectionIndex] : undefined
      // get the elements list to remove from
      const elements = section instanceof SectionElement ? section.elements : form.elements
      //remove the element
      elements.splice(index, 1)
      //update positions
      elements.forEach((el, i) => {
        el.position = i
      })
      // clear selection
      enqueueSelection({ index: -1, sectionIndex: -1 })
      setSelectedElement(undefined)
      // notify update
      updateExternalForm(form)
    }
  }
  const elementDeleteFromBackend = async (index: number, sectionIndex: number | undefined) => {
    const element = getSelectedElement(index, sectionIndex)
    if (!element) return

    const response = await deleteElement({
      id: element.id,
      resourceId: form.resourceId || undefined,
      resourceType: form.resourceType,
    })
    const result = handleResult(response, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    })

    if (result.success) {
      // clear selection
      enqueueSelection({ index: -1, sectionIndex: -1 })
      setSelectedElement(undefined)
      //close the delete element dialog
      setOpenDialog(undefined)
    }
  }

  /** Duplicate the element  */
  const elementDuplicate = async (index: number, sectionIndex: number | undefined) => {
    const section = sectionIndex !== undefined ? form.elements[sectionIndex] : undefined
    const elements = section instanceof SectionElement ? section.elements : form.elements
    const element = elements[index]
    if (!element) throw new Error(`elementDuplicate: element [${index},${sectionIndex}] not found`)

    // Calculate position based on bottom parameter logic
    const insertPosition = calculateInsertPosition(elements)

    // Create a copy of the element for duplication
    const duplicatedElement = elementFromData({
      id: undefined, // Let backend generate the ID
      label: i18n.t('forms.modals.actions.duplicate_label', { label: element.label }),
      description: element.description,
      position: insertPosition,
      mandatory: element.mandatory,
      options: element.options.map((option) => ({
        ...option,
        id: undefined, // Remove option IDs so they get created
      })),
      params: element.params,
      sectionId: element.sectionId || undefined,
      type: element.getType(),
      answer: element.answer || undefined,
      hasCorrectAnswer: element.hasCorrectAnswer,
    })

    elements.splice(insertPosition, 0, duplicatedElement)
    //update positions
    elements.forEach((el, i) => {
      el.position = i
    })

    //enqueue change selection after form loaded
    enqueueSelection({ index: insertPosition, sectionIndex })

    // Update form (will save or notify parent based on mode)
    await onFormUpdated()
  }

  /** Change element  position */
  const elementMove = async (
    command: MoveCommand,
    index: number,
    sectionIndex: number | undefined
  ) => {
    const section = sectionIndex !== undefined ? form.elements[sectionIndex] : undefined
    // Get the array where the element (form or section) moves
    const elements = section instanceof SectionElement ? section.elements : form.elements
    const firstLockedIndex = elements.findIndex((el) => el.isLocked())
    const maxPosition = firstLockedIndex !== -1 ? firstLockedIndex - 1 : elements.length - 1

    const fromPosition = index
    let toPosition: number | undefined = undefined
    if (command == 'top' && fromPosition > 0) {
      toPosition = 0
    } else if (command == 'up' && fromPosition > 0) {
      toPosition = fromPosition - 1
    } else if (command == 'down' && fromPosition < maxPosition) {
      toPosition = fromPosition + 1
    } else if (command == 'bottom' && fromPosition < maxPosition) {
      toPosition = maxPosition
    }

    if (toPosition != undefined) {
      //get & remove the element from the currrent position
      const element = elements.splice(fromPosition, 1)[0]
      if (!element) throw new Error(`Move element: element in position ${fromPosition} not found`)
      //insert the element on the new position
      elements.splice(toPosition, 0, element)
      //update positions
      elements.forEach((el, i) => {
        el.position = i
      })
      //enqueue change selection after form loaded
      enqueueSelection({ index: toPosition, sectionIndex })

      // Update form (will save or notify parent based on mode)
      await onFormUpdated()
    }
  }

  /** The actions that you can 'make' on an element */
  const elementActions = (index: number, sectionIndex: number | undefined): DropdownItem[] => {
    const element = getSelectedElement(index, sectionIndex)
    if (!element) return []

    return element.isLocked()
      ? // No actions if the element is locked
        []
      : // All actions we can make on the element
        [
          {
            label: i18n.t('forms.editor.elements_actions.edit'),
            icon: Pencil,
            onClick: () => setElementToEdit({ index, sectionIndex }),
          },
          {
            label: i18n.t('forms.modals.actions.duplicate'),
            icon: Folders,
            onClick: () => elementDuplicate(index, sectionIndex),
          },
          { type: 'separator' },
          {
            label: i18n.t('forms.editor.elements_actions.move_to_top'),
            icon: ArrowUp,
            onClick: () => elementMove('top', index, sectionIndex),
          },
          {
            label: i18n.t('forms.editor.elements_actions.move_up'),
            icon: ArrowUp,
            onClick: () => elementMove('up', index, sectionIndex),
          },
          {
            label: i18n.t('forms.editor.elements_actions.move_down'),
            icon: ArrowDown,
            onClick: () => elementMove('down', index, sectionIndex),
          },
          {
            label: i18n.t('forms.editor.elements_actions.move_to_bottom'),
            icon: ArrowDown,
            onClick: () => elementMove('bottom', index, sectionIndex),
          },
          { type: 'separator' },
          {
            label: i18n.t('forms.modals.actions.delete'),
            icon: Delete,
            onClick: () => elementDelete(index, sectionIndex),
            critical: true,
          },
        ]
  }

  const renderEmptyState = () => (
    <OneEmptyState
      actions={[
        {
          label: emptyStateText?.buttonLabel || i18n.t('forms.editor.empty_state.button'),
          onClick: () => {
            setAddingToSectionIndex(undefined)
            setElementToCreate(true)
          },
          type: 'default',
          variant: 'outline',
          icon: Pencil,
        },
      ]}
      description={emptyStateText?.description || i18n.t('forms.editor.empty_state.description')}
      emoji="✏️"
      title={emptyStateText?.title || i18n.t('forms.editor.empty_state.title')}
    />
  )

  const renderAllElements = () => (
    <Box gap="s24" width="full" justifyContent="center" alignItems="center">
      {form.elements.map((element: Element, index) => (
        <Box key={element.uuid} style={{ maxWidth: '720px' }} width="full">
          <F0Card otherActions={elementActions(index, undefined)} description={element.getName()}>
            <Box style={{ pointerEvents: 'auto' }}>
              <ElementPreview
                key={element.uuid}
                element={element}
                mode="edit"
                showElementName={showElementName}
                titleSize={titleSize}
              />
            </Box>
          </F0Card>
          {element instanceof SectionElement && (
            <>
              {element.elements.map((child: Element, _childIndex) => (
                <ElementPreview
                  key={child.uuid}
                  element={child}
                  mode="edit"
                  showElementName={showElementName}
                  titleSize={titleSize}
                />
              ))}
              <Box flexDirection="row" alignItems="center">
                <div className="border-solid border-0 border-t border-f1-border-secondary w-full" />
                <Text.Normal whiteSpace="nowrap" className="text-base p-3">
                  {i18n.t('forms.section_end')}
                </Text.Normal>
                <div className="border-solid border-0 border-t border-f1-border-secondary w-full" />
              </Box>
            </>
          )}
        </Box>
      ))}
    </Box>
  )

  const renderSelectedElement = () => {
    if (!selectedElement) return null

    const element = getSelectedElement(selectedElement.index, selectedElement.sectionIndex)
    if (!element) return null

    const headerActions =
      element instanceof SectionElement && !element.isLocked()
        ? [
            {
              label: 'Add question',
              onClick: () => {
                const sectionIdx =
                  selectedElement.sectionIndex !== undefined
                    ? selectedElement.sectionIndex
                    : selectedElement.index
                setAddingToSectionIndex(sectionIdx)
                setElementToCreate(true)
              },
              disabled: false,
              icon: Add,
              hideLabel: false,
              variant: 'outline' as const,
            },
          ]
        : []

    return (
      <>
        <EditorHeader
          metadata={[
            {
              type: 'raw-tag',
              text: element.getName(),
            },
            { type: 'divider' },
            ...(element.isLocked() || element.mandatory
              ? [
                  {
                    type: 'raw-tag' as const,
                    text: element.mandatory
                      ? i18n.t('forms.editor.mandatory_title')
                      : i18n.t('forms.editor.locked_title'),
                    icon: AlertCircleLine,
                  },
                  { type: 'divider' as const },
                ]
              : []),
            isSaving
              ? {
                  type: 'spinner' as const,
                  label: i18n.t('forms.editor.header.saving'),
                }
              : {
                  type: 'text' as const,
                  label: hasUnsavedChanges
                    ? i18n.t('forms.editor.header.changes_pending')
                    : element?.updatedAt
                    ? `${i18n.t('forms.editor.header.last_saved')}: ${formatDateToString(
                        element.updatedAt,
                        locale
                      )} · ${formatTimeToString(element.updatedAt, locale)}`
                    : i18n.t('forms.editor.header.not_saved_yet'),
                },
          ]}
          dropdownActions={
            element.isLocked()
              ? []
              : elementActions(selectedElement.index, selectedElement.sectionIndex)
          }
          actions={headerActions}
        />
        <Box paddingTop="s24" width="full" alignItems="center">
          <ElementPreview
            key={element.uuid}
            element={element}
            mode="edit"
            showElementName={showElementName}
            titleSize={titleSize}
          />
        </Box>
      </>
    )
  }

  const renderEditModal = () => {
    if (!elementToEdit) return null

    const element = getSelectedElement(elementToEdit.index, elementToEdit.sectionIndex)
    if (!element) return null

    return (
      <ElementEditor
        element={element}
        hasCorrectAnswer={form.configuration?.scorable || false}
        onChange={elementUpdated}
        isOpen
        onClose={() => setElementToEdit(undefined)}
        formType={form.formType}
        isCreatingInsideSection={elementToEdit.sectionIndex !== undefined}
        permittedFieldTypes={permittedFieldTypes}
      />
    )
  }

  const mainContent = (
    <>
      {form.getElementCount() === 0 && renderEmptyState()}
      {form.getElementCount() > 0 && (
        <Box
          width="full"
          height="full"
          alignItems="flexStart"
          padding={withContentPadding ? 's16' : undefined}
        >
          <Box gap="s8" width="full">
            {(!selectedElement || showAllElements) && renderAllElements()}
            {selectedElement && !showAllElements && renderSelectedElement()}
          </Box>
        </Box>
      )}
      {renderEditModal()}

      {elementToCreate && (
        <ElementEditor
          hasCorrectAnswer={form.configuration?.scorable || false}
          onChange={handleElementCreate}
          isOpen
          onClose={() => {
            setElementToCreate(false)
            setAddingToSectionIndex(undefined)
            setSearchParams(undefined)
            onCloseAddQuestionModal?.()
          }}
          formType={form.formType}
          isCreatingInsideSection={addingToSectionIndex !== undefined}
          permittedFieldTypes={
            addingToSectionIndex !== undefined
              ? permittedFieldTypes?.filter((type) => type !== 'section')
              : permittedFieldTypes
          }
        />
      )}
    </>
  )

  return (
    <>
      {hideSidebar ? (
        mainContent
      ) : (
        <TwoColumnLayout
          mainColumnPosition="right"
          sideContent={
            <SidebarForm
              form={form}
              onFormUpdated={onFormUpdated}
              selectedElement={selectedElement}
              onSelectElement={selectElement}
              onAddElement={() => {
                setAddingToSectionIndex(undefined)
                setElementToCreate(true)
              }}
            />
          }
        >
          {mainContent}
        </TwoColumnLayout>
      )}

      {openDialog && (
        <DeleteDialog
          open
          onClose={() => setOpenDialog(undefined)}
          onDelete={elementDeleteFromBackend}
          index={openDialog.index}
          sectionIndex={openDialog.sectionIndex}
        />
      )}
    </>
  )
}

/**
 * Wrapper component that uses the cocreation agent hook
 * Only rendered when agentEnabled is true to avoid CopilotKit context errors
 */
const FormEditorWithAgent: React.FC<FormEditorProps & { coCreationFeatureAvailable: boolean }> = (
  props
) => {
  const { state, setState } = useCocreationAgent<AgentStateType, AgentStateType>({
    name: props.coCreationFeatureAvailable ? 'co-creation-workflow' : 'one-workflow',
    initialState: {
      questionnaire: getAiStateFromForm(props.form),
      updateForm: false,
    },
    initialMessage: i18n.t('forms.one.chat_initial_message'),
  })

  return <FormEditorContent {...props} state={state} setState={setState} />
}

/**
 * FormEditor component for creating and editing forms
 * Allows adding different types of elements to a form
 * Checks feature flags first before rendering the editor
 */
const FormEditor: React.FC<FormEditorProps> = (props) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  // Use the same hook that the layout uses to determine if CopilotKit is available
  const { loading: aiAgentLoading, enabled: aiAgentEnabled } = useAiAgentFeature()

  const { data: featureFlagAvailabilities, loading: loadingFeatureFlags } = useQuery(
    FeatureFlagAvailabilitiesDocument,
    {
      variables: {
        companyId,
        featureNames: ['dev_ai_agent', 'dev_forms_ai_cocreation'],
      },
    }
  )

  if (loadingFeatureFlags || aiAgentLoading) {
    return <Loading expand />
  }

  const devAiAgentFlagEnabled =
    featureFlagAvailabilities?.features?.featuresConnection?.nodes.find(
      (feature) => feature.name === 'dev_ai_agent'
    )?.enabled ?? false

  const coCreationFeatureAvailable =
    featureFlagAvailabilities?.features?.featuresConnection?.nodes.find(
      (feature) => feature.name === 'dev_forms_ai_cocreation'
    )?.enabled ?? false

  // Only render the agent wrapper when BOTH conditions are met:
  // 1. CopilotKit is available (useAiAgentFeature - matches layout condition)
  // 2. dev_ai_agent feature flag is enabled
  // This prevents CopilotKit context errors when the provider is not mounted
  if (aiAgentEnabled && devAiAgentFlagEnabled) {
    return (
      <FormEditorWithAgent {...props} coCreationFeatureAvailable={coCreationFeatureAvailable} />
    )
  }

  return (
    <FormEditorContent
      {...props}
      coCreationFeatureAvailable={coCreationFeatureAvailable}
      state={undefined}
      setState={undefined}
    />
  )
}

export default FormEditor
